import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { isServer } from '../utils/isServer';

interface ElementObserverContextType {
  onElementVisible: (element: HTMLElement, callback: () => void) => () => void;
}

const ElementObserverContext = createContext<ElementObserverContextType>({
  onElementVisible: (_, callback) => {
    callback();
    return () => {};
  },
});

export const useElementObserver = () => useContext(ElementObserverContext);

type Callback = () => void;

type ElementCallbackQueue = Array<{
  element: HTMLElement;
  callback: Callback;
}>;

const createCallbackQueue = (
  wrapCallbacks: (runCallbacks: () => void) => void = (runCallbacks) => runCallbacks()
) => {
  let queue: Callback[] = [];

  let currentTimeout: ReturnType<typeof setTimeout> | null = null;

  const clearCurrentTimeout = () => {
    if (currentTimeout) {
      clearTimeout(currentTimeout);
    }
    currentTimeout = null;
  };

  const scheduleCallbacks = () => {
    clearCurrentTimeout();

    currentTimeout = setTimeout(() => {
      const currentQueue = queue;
      queue = [];

      wrapCallbacks(() => {
        currentQueue.forEach((callback) => {
          callback();
        });
      });
    }, 10);
  };

  return {
    add: (callback: Callback) => {
      queue.push(callback);

      scheduleCallbacks();
    },
    remove: (callback: Callback) => {
      queue = queue.filter((c) => c !== callback);
    },
    destroy: () => {
      clearCurrentTimeout();
    },
  };
};

const removeEntryFromQueueByElement = (
  e: HTMLElement,
  queue: ElementCallbackQueue
): ElementCallbackQueue => queue.filter(({ element }) => element !== e);

export const ElementObserverProvider: React.FC<React.PropsWithChildren<{ threshold?: number }>> = ({
  children,
  threshold = 0.2,
}) => {
  const queue = useRef<ElementCallbackQueue>([]);

  const [toBeCalledQueue] = useState(() => createCallbackQueue(ReactDOM.unstable_batchedUpdates));

  useEffect(() => {
    return () => {
      toBeCalledQueue.destroy();
    };
  }, [toBeCalledQueue]);

  const [elementObserver] = useState(() => {
    if (!isServer() && window.IntersectionObserver) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const queueEntry = queue.current.find(({ element }) => element === entry.target);

              if (queueEntry) {
                toBeCalledQueue.add(queueEntry.callback);
                queue.current = removeEntryFromQueueByElement(queueEntry.element, queue.current);
              }

              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: threshold }
      );

      return observer;
    }
  });

  const contextValue = useRef<ElementObserverContextType>({
    onElementVisible: (element, callback) => {
      if (elementObserver) {
        queue.current.push({ element, callback });
        elementObserver.observe(element);

        return () => {
          queue.current = removeEntryFromQueueByElement(element, queue.current);
          toBeCalledQueue.remove(callback);
          if (elementObserver) {
            elementObserver.unobserve(element);
          }
        };
      }

      callback();
      return () => {};
    },
  });

  return (
    <ElementObserverContext.Provider value={contextValue.current}>
      {children}
    </ElementObserverContext.Provider>
  );
};
